import { FlowPage } from '@monthio/design-system'
import {
	eventHubService,
	LoadingScreen,
	LoadingSpinner,
	trackingService,
} from '@monthio/react-components'
import config from '#config'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type {
	ApplicantDto,
	ApplicantSessionDto,
	ConsentRequestDto,
	ConsumerInputs,
	EskatCompletedEvent,
	EskatConsentState,
	EskatDataFetchingState,
	EskatError,
} from './Eskat.types'

import { useDemoProfile } from '../../modulesTemp/shared/useDemoProfile'
import { DemoEnvConsent } from './Components/DemoEnvConsent/DemoEnvConsent'
import { EskatConsentError } from './Components/EskatConsentError'
import { EskatConsentIntro } from './Components/EskatConsentIntro'
import { EskatDummyDataConsent } from './Components/EskatDummyDataConsent'
import { EskatSuccessModal } from './Components/EskatSuccessDialog'
import {
	MitIdLoginMockUpPage,
	RenderInWindow,
} from './Components/MitIdLoginMockPage'
import { EskatApiEnvironment, EskatDataFetchingStatusEnum } from './Eskat.types'
import { useEskatModule } from './useEskatModule'

export interface EskatConsentProps {
	eskatConsentState: EskatConsentState
	applicant?: ApplicantDto
	applicantSession?: ApplicantSessionDto
	createEskatConsent: () => void
	setError: (error: EskatError | null) => void
	history: History
	children?: never
	error: EskatError | null
}

const ONE_MINUTE = 60e3

export const Eskat = (): JSX.Element => {
	const { config: moduleConfig, finish, data } = useEskatModule()
	const [consentState, setConsentState] = useState<EskatConsentState | null>(
		null,
	)
	const [error, setError] = useState<EskatError | null>(null)

	const [fetchingState, setFetchingState] = useState<EskatDataFetchingState>({
		status: EskatDataFetchingStatusEnum.IDLE,
		error: undefined,
	})

	const eskatFormRef = useRef<HTMLFormElement>(null)
	const { t } = useTranslation()
	const demoProfile = useDemoProfile()
	const [isMitIdMockPageOpen, setIsMitIdMockPageOpen] = useState(false)

	const handleOpenMitIdMockPage = useCallback(
		() => setIsMitIdMockPageOpen(true),
		[],
	)

	const [consent, setConsent] = useState<ConsentRequestDto>({
		consentRequest: consentState?.consentRequest ?? '',
		environment: consentState?.environment ?? EskatApiEnvironment.Production,
	})

	useEffect(() => {
		setConsent(consentState as ConsentRequestDto)
	}, [consentState])

	const [successMessageOpen, setSuccessMessageOpen] = useState(false)

	const eskatUrls = new Map<EskatApiEnvironment, string>([
		[
			EskatApiEnvironment.Production,
			'https://samtykke.skat.dk/samtykke/anmodadgang.do',
		],
		[
			EskatApiEnvironment.Demo,
			'https://samtykke.demo.skat.dk/samtykke/anmodadgang.do',
		],
		[
			EskatApiEnvironment.Mock,
			`${config.eskatApiUrl}/consent/mockWindow/${data.applicantSessionId}`,
		],
	])
	useEffect(() => {
		if (fetchingState.status == EskatDataFetchingStatusEnum.FETCH_FAILURE) {
			setError({
				titleKey: 'eskat-data-fetching-error-title',
				descriptionKey: 'eskat-data-fetching-error-description',
			})
		}

		if (fetchingState.status == EskatDataFetchingStatusEnum.FETCH_SUCCESS) {
			setSuccessMessageOpen(true)
			setTimeout(() => setSuccessMessageOpen(false), 1500)
			setTimeout(() => {
				finish()
			}, 2000)
		}
	}, [fetchingState.status, finish])

	useEffect(() => {
		let isMounted = true

		if (data.isFinished) {
			eventHubService.onEvent({
				source: 'eskatClient',
				topic: 'moduleCompleted',
				payload: { module: 'eskat', skipped: true },
			})
		}

		const initiateNewConsent = async () => {
			try {
				const result = await moduleConfig.getConsent()
				setConsentState(result)
			} catch (error_) {
				trackingService.trackTechnicalEvent('eskatConsentRefreshFailed', {
					error: error_,
				})
			}
		}

		const initiateFetchData = async () => {
			try {
				setFetchingState({
					status: EskatDataFetchingStatusEnum.FETCHING,
					error: undefined,
				})

				await moduleConfig.fetchAndPersistEskatData()
				setFetchingState({
					status: EskatDataFetchingStatusEnum.FETCH_SUCCESS,
					error: undefined,
				})
			} catch (error_: any) {
				setFetchingState({
					status: EskatDataFetchingStatusEnum.FETCH_FAILURE,
					error: {
						errorCode: error_.errorCode,
						errorText: error_.name + ': ' + error_.message,
						innerErrors: error_.innerErrors ?? [],
					},
				})
			}
		}

		const goForwardAndTrackEvent = () => {
			trackingService.trackEvent('eskatConsentGiven', {
				sessionId: data.applicantSessionId,
			})

			finish()
		}
		const setConsentErrors = (eventData?: EskatCompletedEvent) => {
			if (isMounted) {
				const returnCode = eventData ? eventData.returnCode : undefined
				const subErrorCode = eventData ? eventData.subErrorCode : undefined
				setError({
					titleKey: 'eskat-consent-error-title',
					descriptionKey:
						returnCode != undefined
							? `eskat-consent-error-${returnCode}`
							: 'eskat-consent-error-general',
					errorCodeDescriptionKey: 'eskat-consent-errorCode-description',
					errorCode: subErrorCode,
				})
			}
		}

		const setCprMismatchErrors = async (currentEskatCpr: string) => {
			// Check other coApplicants
			// GZE: this can also be moved as startdata
			const applicants = data.caseId
				? await moduleConfig.getApplicantsByCaseId(data.caseId)
				: undefined

			const _error: EskatError = {
				titleKey: 'eskat-error-cprMismatch-title',
				descriptionKey: 'eskat-error-cprMismatch-description',
			}

			if (!applicants) {
				setError(_error)
				return
			}

			// Check other coApplicants
			const coApplicants = applicants.filter(
				(a) => a.id != data.applicantId && a.finishedOn != undefined,
			)

			for (const coApplicant of coApplicants) {
				const coApplicantConsumerInputs: ConsumerInputs =
					coApplicant?.consumerInputJson
						? JSON.parse(coApplicant?.consumerInputJson)
						: undefined

				const coApplicantCpr =
					coApplicantConsumerInputs?.personalInformation?.hashedCprNumber ??
					coApplicant?.hashedConsumerCpr

				if (coApplicantCpr === currentEskatCpr) {
					_error.descriptionKey =
						'eskat-error-cprMismatch-coApplicant-description'
					break
				}
			}

			setError(_error)
		}

		const handleMonthioEskatConsentMessage = async (
			eventData: EskatCompletedEvent,
		) => {
			switch (eventData.returnCode) {
				case 'Ok':
					if (
						showCprValidationError(
							data.applicantSessionQuestionnaireHashedCpr,
							data.applicantClientInputHashedCpr,
							eventData.hashedCpr,
						)
					) {
						trackingService.trackEvent('eskatCprMismatched', {
							sessionId: data.applicantSessionId,
						})
						await setCprMismatchErrors(eventData.hashedCpr)
						await initiateNewConsent()
					} else await initiateFetchData()
					return

				case 'EndUserCancelled':
					trackingService.trackEvent('eskatConsentCancelledByUser', {
						sessionId: data.applicantSessionId,
					})
					setConsentErrors(eventData)
					return

				default:
					switch (eventData.eskatErrorCode) {
						case 'S000':
							goForwardAndTrackEvent()
							return
						case 'S014':
						case 'S019':
							await initiateNewConsent()
					}
					setConsentErrors(eventData)
			}
		}

		const unsubscribe = moduleConfig.registerEskatMessageListener(
			handleMonthioEskatConsentMessage,
		)

		return () => {
			isMounted = false
			unsubscribe()
		}
	}, [])

	useEffect(() => {
		if (moduleConfig.getConsent == null) {
			return
		}

		const initiateNewConsent = async () => {
			setConsentState(null)

			try {
				const result = await moduleConfig.getConsent()
				setConsentState(result)
			} catch (_error) {
				setConsentState(null)
			}
		}
		initiateNewConsent()

		const renewTransactionInterval = setInterval(
			initiateNewConsent,
			ONE_MINUTE * 2,
		)

		return () => {
			clearInterval(renewTransactionInterval)
		}
	}, [moduleConfig.getConsent])

	const showCprValidationError = (
		consumerInputsCpr: string | undefined | null,
		clientInputsCpr: string | undefined | null,
		eskatCpr: string,
	) => {
		if (moduleConfig.environment === 'test') {
			return false
		}

		if (
			consumerInputsCpr &&
			clientInputsCpr &&
			consumerInputsCpr !== clientInputsCpr
		) {
			return true
		}
		const otherCpr = consumerInputsCpr ?? clientInputsCpr

		if (!otherCpr) {
			return false
		}

		return otherCpr != eskatCpr
	}

	const handleOpenPopup = useCallback(() => {
		setError(null)
		moduleConfig.openPopup(eskatFormRef)
	}, [eskatFormRef])

	const isLoading =
		fetchingState.status === EskatDataFetchingStatusEnum.FETCHING

	const isDemoProfile =
		(moduleConfig.environment == 'development' ||
			moduleConfig.environment == 'test') &&
		demoProfile !== null
	const shouldShowDemoConsent =
		(moduleConfig.environment == 'development' ||
			moduleConfig.environment == 'test') &&
		!isDemoProfile

	const handlePostTestDataClick = useCallback(() => {
		setIsMitIdMockPageOpen(false)
		return moduleConfig.postTestData(demoProfile).then(() => {
			finish()
		})
	}, [demoProfile, finish])

	// TODO: Verify this can be removed or type is lying.
	if (!consent) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	return (
		<FlowPage>
			<FlowPage.Content $spacing="xl">
				{error ? (
					<EskatConsentError error={error} />
				) : (
					<EskatConsentIntro environment={consent?.environment} />
				)}

				<EskatDummyDataConsent
					environment={moduleConfig.environment}
					clientId={data.clientId}
					handlePostTestDataClick={handlePostTestDataClick}
				/>
				{shouldShowDemoConsent && <DemoEnvConsent setConsent={setConsent} />}
			</FlowPage.Content>
			<form
				ref={eskatFormRef}
				method="post"
				action={eskatUrls.get(
					consent?.environment ?? EskatApiEnvironment.Production,
				)}
				target="eskat-iframe"
			>
				<input
					type="hidden"
					name="SAMTYKKEPARM"
					value={consent.consentRequest}
				/>
				<input type="hidden" name="TESTMODE" value="NEJ" />
			</form>
			<EskatSuccessModal open={successMessageOpen} />
			{isMitIdMockPageOpen && (
				<RenderInWindow>
					<MitIdLoginMockUpPage onContinueClick={handlePostTestDataClick} />
				</RenderInWindow>
			)}
			<FlowPage.Footer
				actions={
					<FlowPage.Cta
						id="btn-eskat-popup"
						onClick={isDemoProfile ? handleOpenMitIdMockPage : handleOpenPopup}
						disabled={isLoading}
					>
						{error ? (
							t('consent-eksat-popupLink-retry')
						) : (
							<AsyncActionContent isLoading={isLoading}>
								{t('consent-eksat-popupLink')}
							</AsyncActionContent>
						)}
					</FlowPage.Cta>
				}
			/>
		</FlowPage>
	)
}

const SpinnerContainer = styled.div`
	position: relative;
	content: '';
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
`

const Spinner = styled(LoadingSpinner)<{ $isVisible?: boolean }>`
	display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);

	& > * {
		// This is a HACK to make the spinner white. Remove as soon as a better solution is available!
		--primary: white;
	}
`

const LabelContainer = styled.span<{ $hidden?: boolean }>`
	visibility: ${(props) => (props.$hidden ? 'hidden' : 'visible')};
`

interface AsyncActionContentProps {
	isLoading: boolean
	children: string
}

const AsyncActionContent = ({
	isLoading,
	children,
}: AsyncActionContentProps) => {
	return (
		<SpinnerContainer>
			<Spinner $isVisible={isLoading} />
			<LabelContainer $hidden={isLoading}>{children}</LabelContainer>
		</SpinnerContainer>
	)
}
